import React from "react";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

const StatusBadge = ({ value, errorMessage }: { value?: string; errorMessage?: string }) => {
  if (!value) return null;
  const variant =
    value === "Processed" ? "info" : value === "Reviewed" ? "success" : "warning";

  const badge = <Badge variant={variant}>{value}</Badge>;

  if (value === "Error" && errorMessage) {
    const displayError = errorMessage
      .replace(/^Error occurred\. Exception:\s*/i, "")
      .slice(0, 300);
    return (
      <TooltipProvider>
        <Tooltip delayDuration={200}>
          <TooltipTrigger asChild>{badge}</TooltipTrigger>
          <TooltipContent className="max-w-sm text-xs whitespace-pre-wrap">
            <p>{displayError}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return badge;
};

export interface DocumentMetaBlockProps {
  filename?: string;
  authors?: string;
  countries?: string;
  year?: string;
  sourceOrganisation?: string;
  status?: string;
  errorMessage?: string;
}

export const DocumentMetaBlock = ({
  filename,
  authors,
  countries,
  year,
  sourceOrganisation,
  status,
  errorMessage,
}: DocumentMetaBlockProps) => {
  const metaParts: React.ReactNode[] = [];
  if (countries)
    metaParts.push(
      <span key="country">
        <span className="font-semibold">Country:</span> {countries}
      </span>
    );
  if (year)
    metaParts.push(
      <span key="year">
        <span className="font-semibold">Year:</span> {year}
      </span>
    );
  if (sourceOrganisation)
    metaParts.push(
      <span key="org">
        <span className="font-semibold">Source Org:</span> {sourceOrganisation}
      </span>
    );

  return (
    <>
      {filename && (
        <p className="text-xs text-muted-foreground mt-2">
          <span className="font-semibold">File:</span> {filename}
        </p>
      )}
      {authors && (
        <p className="text-xs text-muted-foreground">
          <span className="font-semibold">Author(s):</span> {authors}
        </p>
      )}
      {metaParts.length > 0 && (
        <p className="text-xs text-muted-foreground">
          {metaParts.reduce<React.ReactNode[]>((acc, part, i) => {
            if (i > 0) acc.push(<span key={`sep-${i}`}> • </span>);
            acc.push(part);
            return acc;
          }, [])}
        </p>
      )}
      <div className="mt-2">
        <StatusBadge value={status} errorMessage={errorMessage} />
      </div>
    </>
  );
};
